<script setup lang="ts" generic="T extends { id: number, name: string }">
  import { ChevronDown, Loader2, X } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, ref } from 'vue'

  const props = defineProps<{
    modelValue: T | null,
    items: T[],
    loading?: boolean,
    placeholder?: string,
    searchKeys: Array<keyof T>,
    displayFormat?: (item: T) => string,
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: T | null): void,
  }>()

  const dropdownRef = ref<HTMLElement | null>(null)
  const showDropdown = ref(false)
  const searchTerm = ref('')

  // Filter items based on the search term
  const filteredItems = computed(() => {
    const lowerCaseSearch = searchTerm.value.trim().toLowerCase()
    if (!lowerCaseSearch) return props.items

    return props.items.filter(item =>
      props.searchKeys.some(key =>
        String(item[key] || '').toLowerCase().includes(lowerCaseSearch),
      ),
    )
  })

  const getDisplayText = (item: T) => {
    return props.displayFormat ? props.displayFormat(item) : item.name
  }

  const selectItem = (item: T | null) => {
    emit('update:modelValue', item)
    searchTerm.value = item ? getDisplayText(item) : ''
    showDropdown.value = false
  }

  const toggleDropdown = () => {
    showDropdown.value = !showDropdown.value
    if (showDropdown.value) {
      searchTerm.value = '' // Clear search when opening
    } else if (props.modelValue) {
      searchTerm.value = getDisplayText(props.modelValue) // Restore selected name when closing
    }
  }

  const clearSelection = (event: MouseEvent) => {
    event.stopPropagation()
    selectItem(null)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
      showDropdown.value = false
      searchTerm.value = props.modelValue ? getDisplayText(props.modelValue) : ''
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
</script>

<template>
  <div
    ref="dropdownRef"
    class="relative w-full">
    <div
      class="relative flex items-center border rounded cursor-pointer"
      @click="toggleDropdown">
      <input
        v-model="searchTerm"
        type="text"
        :placeholder="modelValue ? getDisplayText(modelValue) : placeholder"
        class="pl-3 capitalize font-medium pr-10 placeholder:font-normal placeholder:normal-case placeholder:text-black py-2 block flex-1 rounded text-sm text-gray-700 focus-visible:outline-primary focus-visible:ring-0"
        @input="showDropdown = true">

      <app-button
        v-if="modelValue"
        :tabindex="-1"
        class="absolute right-8 text-gray-400 hover:text-red-500"
        @click.stop="clearSelection">
        <x class="size-4 text-red-500" />
      </app-button>

      <div class="absolute right-2 flex items-center">
        <loader2
          v-if="loading"
          class="animate-spin size-4 text-red-500" />
        <chevron-down
          v-else
          class="size-4 transition-transform text-gray-500"
          :class="{ 'rotate-180': showDropdown }" />
      </div>
    </div>

    <div
      v-show="showDropdown"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
      <app-button
        class="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b hidden"
        :class="{ 'bg-blue-50 text-blue-600 font-medium': !modelValue }"
        @click="selectItem(null)">
        <span class="font-medium text-sm">{{ placeholder || 'Semua' }}</span>
      </app-button>

      <div
        v-if="loading"
        class="flex items-center justify-center py-4">
        <loader2 class="h-4 w-4 animate-spin text-blue-500" />
        <span class="ml-2 text-sm text-gray-500">Memuat data...</span>
      </div>
      <div
        v-else-if="filteredItems.length === 0"
        class="p-4 text-center text-gray-500 text-sm">
        Data tidak ditemukan
      </div>
      <div v-else>
        <app-button
          v-for="item in filteredItems"
          :key="item.id"
          class="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none capitalize"
          :class="{ 'bg-blue-50 text-blue-600 font-medium': modelValue?.id === item.id }"
          @click="selectItem(item)">
          {{ getDisplayText(item) }}
        </app-button>
      </div>
    </div>
  </div>
</template>
